import { useState } from 'react';
import { Check, Circle, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { RowCrmAction } from '@/components/views/atoms/RowCrmAction';
import { invokeDashboardChat } from '@/lib/dashboard-chat';
import { rerunAppRecordById } from '@/lib/app-rerun';
import type { DashboardChatMessage } from '@shared/types/dashboard-chat';
import type { DashboardChatGestureResult, GestureLine } from '@shared/types/gestures';

interface AppChatGestureCardProps {
  conversationId: string | null;
  messageId: string;
  result: DashboardChatGestureResult;
  onMessages: (messages: DashboardChatMessage[]) => void;
}

const LineIcon = ({ status }: { status: GestureLine['status'] }) => {
  const icon = 'mt-0.5 h-4 w-4 shrink-0';
  if (status === 'done') return <Check className={`${icon} text-domino-aubergine`} aria-label="Fait" />;
  if (status === 'failed') return <X className={`${icon} text-domino-inkSoft`} aria-label="Pas fait" />;
  if (status === 'pending') return <Circle className={`${icon} text-domino-inkSoft`} aria-label="À confirmer" />;
  return <Minus className={`${icon} text-domino-inkSoft`} aria-label="Pas tenté" />;
};

/** « Relancer l'analyse » as the record's drawer does it: run-flow from the browser, the person's click. */
const RerunControl = ({ flowId, recordId }: { flowId: string; recordId: string }) => {
  const [state, setState] = useState<'idle' | 'running' | 'started' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  if (state === 'started') return <p className="mt-1 text-xs text-domino-inkSoft">Analyse relancée.</p>;
  return (
    <div className="mt-1">
      <Button size="sm" variant="secondary" loading={state === 'running'} disabled={state === 'running'}
        onClick={() => {
          setState('running');
          void rerunAppRecordById(flowId, recordId)
            .then(() => setState('started'))
            .catch((cause: unknown) => { setState('failed'); setError(cause instanceof Error ? cause.message : 'L’analyse n’a pas pu être relancée.'); });
        }}>
        Relancer
      </Button>
      {error && <p role="alert" className="mt-1 text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};

/**
 * Piloter au chat (P1–P2, 04/11/2026): what the chat did for the person, one line per gesture, and
 * « Annuler » for what was done. A gesture that costs or goes outside waits with its own « Confirmer »;
 * « Ajouter au CRM » is the screen's own control, with its preview of what HubSpot gets. The server
 * reads the steps and arguments back from its own message.
 */
export const AppChatGestureCard = ({ conversationId, messageId, result, onMessages }: AppChatGestureCardProps) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (key: string, body: { action: 'undo_gestures' } | { action: 'confirm_gesture'; index: number }) => {
    if (!conversationId) return;
    setBusy(key);
    setError(null);
    try {
      const response = await invokeDashboardChat({ ...body, conversationId, messageId });
      onMessages(response.messages);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Ce geste n’a pas pu être fait.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-xl border border-domino-cardBorder bg-domino-white p-3">
      <ul className="space-y-2">
        {result.lines.map((line, index) => (
          <li key={`${line.gesture}-${index}`} className="flex items-start gap-2 text-sm">
            <LineIcon status={line.status} />
            <div className="min-w-0 flex-1">
              <span className={line.status === 'done' && !result.undone ? 'text-domino-ink' : line.status === 'pending' ? 'text-domino-ink' : 'text-domino-inkSoft'}>
                {line.status === 'failed' ? line.error ?? 'Pas fait.' : line.status === 'not_tried' ? 'Pas tenté.' : line.label}
                {line.status === 'done' && line.error && <span className="block text-xs text-domino-inkSoft">{line.error}</span>}
              </span>
              {line.status === 'pending' && !result.undone && (line.screen?.kind === 'crm_add_company'
                ? <RowCrmAction sourceRunId={line.screen.sourceRunId} rowId={line.screen.rowId} rowLabel={line.screen.rowLabel} className="mt-1" />
                : line.screen?.kind === 'record_rerun'
                ? <RerunControl flowId={line.screen.flowId} recordId={line.screen.recordId} />
                : (
                  <div className="mt-1">
                    <Button size="sm" variant="secondary" loading={busy === `confirm-${index}`} disabled={busy !== null || !conversationId}
                      onClick={() => { void send(`confirm-${index}`, { action: 'confirm_gesture', index }); }}>
                      Confirmer
                    </Button>
                  </div>
                ))}
            </div>
          </li>
        ))}
      </ul>
      {result.undone ? (
        <p className="text-xs text-domino-inkSoft">
          Annulé{result.undone.failed.length ? `, sauf : ${result.undone.failed.join(' ; ')}` : ''}
        </p>
      ) : result.undo.length > 0 && (
        <Button size="sm" variant="ghost" loading={busy === 'undo'} disabled={busy !== null || !conversationId} onClick={() => { void send('undo', { action: 'undo_gestures' }); }}>Annuler</Button>
      )}
      {error && <p role="alert" className="text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};
